import React from 'react';
import { Box, Typography, Link, Avatar, ButtonBase } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Refresh } from '@mui/icons-material';
import dayjs from 'dayjs';
import { EventInfoCardProps } from '../utils/SingleEventAnalysisTab.types';
import { green } from '@mui/material/colors';
import { Stack } from '@mui/system';
import { dataProvider } from '../../../../../DataProvider';
import { EventItem, RawEventData } from '../../../models/event';
import { DeliveryMethodColors } from '../../../../../utils/enum';

/**
 * EventInfoCard Component
 *
 * Presentational component that displays event information including:
 * - Event name and site ID
 * - Event date and time
 * - Category and performer
 * - Venue and location
 * - Analysis completion status
 *
 * @param props - Component props containing event data and handlers
 * @returns Event information display card
 */
const EventInfoCard: React.FC<EventInfoCardProps> = ({
    aiScoring,
    selectedEvent,
    setSelectedEvent,
    analysisCompleted,
    onVenueMapClick,
    setAiScoring,
    setSeatRecommendations,
    getAnalysisEventScore,
    getSeatRecommendations
}) => {
    // Handler for refresh button click
    const handleRefresh = async () => {
        // First, fetch the latest data report for the event
        // Construct report ID based on event properties
        // let reportId = selectedEvent.type + '-' + selectedEvent.siteId + '-' + dayjs(selectedEvent.eventDate).utc().format('YYYYMMDD');
        let reportId = selectedEvent.siteId;
        const rawEventData = (await dataProvider.fetchData('RTP_Event', `GetOneBySite?id=${reportId}`))?.data as RawEventData;
        // Check if data is valid, if not, notify user
        if (!rawEventData) {
            alert('Failed to refresh data: Event data not found.');
            return;
        }
        const eventItem = EventItem.fromRawEventData(rawEventData);
        // Update goTickets, primary, ticketNetwork from eventItem response to selectedEvent
        const updatedEvent = {
            ...selectedEvent,
            goTickets: eventItem.goTickets,
            primary: eventItem.primary,
            ticketNetwork: eventItem.ticketNetwork
        };
        // Update selected event in parent component
        setSelectedEvent(updatedEvent);

        // Now, fetch updated AI scoring
        let aiScore = await getAnalysisEventScore(eventItem);
        setAiScoring(aiScore);
        // Now, fetch updated seat recommendations
        let seatRecs = await getSeatRecommendations(EventItem.fromPrepareData(aiScore.prepareData!), aiScore.analysisId || 0);
        setSeatRecommendations(seatRecs);
    };

    const delayDays = selectedEvent.delayDays ?? aiScoring?.prepareData?.delayDays;
    const deliveryMethod = selectedEvent.deliveryMethod ?? aiScoring?.prepareData?.deliveryMethod;
    const eventCity = aiScoring?.prepareData?.event?.city?.trim() || selectedEvent.city || 'Unknown City';
    const eventState = aiScoring?.prepareData?.event?.state?.trim() || selectedEvent.state || 'Unknown State';
    const eventCountry = aiScoring?.prepareData?.event?.country?.trim() || 'US';

    return (
        <>
            {/* Header Section */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
                pb: 2,
                borderBottom: '1px solid rgba(255,255,255,0.2)'
            }}>
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 'bold',
                            mb: 0.5,
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                        }}
                    >
                        <Link
                            href={selectedEvent.primary?.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            underline='hover'
                            sx={{
                                color: '#1976d2',
                                fontWeight: '400',
                                '&:hover': {
                                    color: 'lightgreen',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            {selectedEvent.name}
                        </Link>
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            opacity: 0.9,
                            fontWeight: 500,
                            lineHeight: 1.6,
                            '& span': { fontWeight: 600, color: 'primary.main' },
                        }}
                    >
                        <span>Site ID:</span> {selectedEvent.siteId}
                        <br />
                        <span>Market Type:</span> {selectedEvent.type}
                        {!!delayDays && delayDays > 0 && (
                            <>
                                &nbsp;|&nbsp;<span>Delay Days:</span> {delayDays}
                            </>
                        )}
                        <br />
                        <span>On Sale Date:</span>{' '}
                        {dayjs(selectedEvent.primary?.oldestTimestamp?.replace(/z/i, ''))
                            .format('MMM DD, YYYY h:mm A')}
                        {(() => {
                            let deliveryInfo = DeliveryMethodColors.find(x => x.id === deliveryMethod);
                            return deliveryInfo !== null && deliveryInfo !== undefined && (
                                <>
                                    &nbsp;|&nbsp;<span title={deliveryInfo.enum} style={{
                                        backgroundColor: deliveryInfo.color,
                                        fontSize: '11px',
                                        padding: '3px',
                                        borderRadius: '3px',
                                        color: 'white'
                                    }}>{deliveryInfo.name }</span>
                                </>
                            );
                        })()}
                    </Typography>
                </Box>
                {analysisCompleted && (
                    <Stack direction="row" spacing={1}>
                        <Avatar sx={{ bgcolor: green[500] }} title='Analysis Complete'><CheckCircleIcon /></Avatar>
                        {/* <Avatar sx={{ bgcolor: '#1976d2' }} title='Refresh Data'><Refresh /></Avatar> */}
                        {selectedEvent.isFromHistory &&
                        <ButtonBase
                            onClick={handleRefresh}
                            aria-label="Refresh data"
                            sx={{
                                borderRadius: '50%',
                                p: 0,
                                '&:focus-visible': {
                                    outline: '2px solid #1976d2',
                                    outlineOffset: '2px',
                                },
                            }}
                        >
                            <Avatar sx={{ bgcolor: '#1976d2' }} title="Force Refresh Data">
                                <Refresh />
                            </Avatar>
                        </ButtonBase>
                        }
                    </Stack>
                    
                )}
            </Box>
            {/* Event Details Grid */}
            <Box sx={{
                display: 'flex', gap: 2, width: '100%'
            }}>
                {/* Date & Time - Top Left */}
                <Box sx={{
                    borderRadius: 3,
                    p: 1,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)',
                    border: '1px solid rgba(63, 81, 181, 0.2)',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(63, 81, 181, 0.15)'
                    }
                }}>
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            letterSpacing: 1.2,
                            color: 'primary.main',
                            mb: 1
                        }}
                    >
                        📅 Event Date
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 'bold',
                            mt: 1,
                            color: 'text.primary',
                            fontSize: '0.9rem'
                        }}
                    >
                        {dayjs(selectedEvent.dateUtc?.replace(/z/i, '')).format('dddd, MMM DD, YYYY')}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 1,
                            color: 'text.secondary',
                            fontWeight: 500
                        }}
                    >
                        🕐 {dayjs(selectedEvent.dateUtc?.replace(/z/i, '')).format('h:mm A')}
                    </Typography>
                </Box>

                {/* Category & Performer - Top Right */}
                <Box sx={{
                    borderRadius: 3,
                    p: 1,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)'
                    }
                }}>
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            letterSpacing: 1.2,
                            color: 'success.main',
                            mb: 1
                        }}
                    >
                        🎭 Category
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 'bold',
                            mt: 1,
                            color: 'text.primary',
                            fontSize: '1rem'
                        }}
                    >
                        {selectedEvent.categoryName || 'Concert'}
                    </Typography>
                    {selectedEvent.performerName && (
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 1,
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                fontWeight: 500
                            }}
                        >
                            🎤 {selectedEvent.performerName}
                        </Typography>
                    )}
                </Box>

                {/* Venue & Location */}
                <Box sx={{
                    borderRadius: 3,
                    p: 1,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%)',
                    border: '1px solid rgba(255, 152, 0, 0.2)',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    display: 'flex',
                    flex: 2,
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(255, 152, 0, 0.15)'
                    }
                }}
                title={'Click to view venue map'}
                onClick={() => onVenueMapClick(selectedEvent.venueId || 0, selectedEvent.smEventVenue || selectedEvent.venue)}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            letterSpacing: 1.2,
                            color: 'warning.main',
                            mb: 1
                        }}
                    >
                        🏟️ Venue
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 'bold',
                            mt: 1,
                            color: 'text.primary',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            '&:hover': {
                                color: 'primary.main',
                                textDecoration: 'underline'
                            }
                        }}
                        title={`${selectedEvent.venue || 'N/A'}`}
                    >
                        {selectedEvent.venue || 'N/A'}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            mt: 1,
                            color: 'text.secondary',
                            fontWeight: 600,
                            fontSize: '0.9rem'
                        }}
                    >
                        📍 {[
                            eventCity,
                            eventState,
                            eventCountry
                        ]
                            .filter(Boolean)
                            .join(', ')}
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

export default EventInfoCard;
